import { type FC, useState } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useIsFetching } from '@tanstack/react-query'

import { useDebounce } from 'react-use'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Navigation from '@segment/templates/Navigation'
import Footer from '@segment/templates/Footer/Footer'
import GettingStarted from '@segment/templates/GettingStarted'

import { useFeaturedRecipesQuery } from '@segment/api/routes/recipes/queries'

import Layout from '@segment/next-shared/layout'

import Hero from './components/Hero'
import Filters from './components/Filters'
import RecipeDivider from './components/RecipeDivider'

import useFilters from './components/Filters/useFilters'
import FeaturedRecipes from './components/FeaturedRecipes'
import RecipesGrid, { useRecipesGrid } from './components/RecipesGrid'

import ogImagePng from './assets/OgRecipes.png'

const copy = {
  pageTitle: `Twilio Segment Recipes`,
  pageDescription: `Explore top use cases for Twilio Segment and the tools that power them. Implement Segment once, integrate 250+ tools with the flip of a switch, and unlock thousands of new uses cases for marketing, personalization, analytics, and more.`,
}

const getCardsSectionTitle = (query: string, filtersString: string) => {
  if (query === `` && filtersString === ``) {
    return `All recipes`
  }

  if (query === ``) {
    return null
  }

  return `Search results for "${query}" in recipes`
}

const RecipesView: FC = () => {
  const theme = useTheme()
  const [query, setQuery] = useState(``)
  const [debouncedQuery, setDebouncedQuery] = useState(query)
  useDebounce(() => setDebouncedQuery(query), 200, [query])

  const { updateFilters, filtersString } = useFilters()

  const ongoingFetches = useIsFetching({ queryKey: [`recipes`] })
  const isQueryLoading = ongoingFetches > 0
  const { data: featuredRecipes } = useFeaturedRecipesQuery({
    args: [{ max: 3 }],
  })

  const { flattenedPages, isEmpty, nextPageTrigger, hasNextPage } = useRecipesGrid({
    query: debouncedQuery,
    filters: filtersString,
  })

  const showFeaturedRecipes = debouncedQuery === `` && filtersString === `` && featuredRecipes

  const showFooter = !hasNextPage

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={copy.pageTitle}
        pageDescription={copy.pageDescription}
        pageOG={ogImagePng}
        bodyBackground={theme.palette.neutrals.white[100]}
        themeColor={theme.palette.neutrals.white[100]}
      >
        <Navigation />
        <Hero query={query} onQueryChange={setQuery} isQueryLoading={isQueryLoading} />
        <Main>
          <Filters onFilterChange={updateFilters} />
          {showFeaturedRecipes && (
            <>
              <FeaturedRecipes recipes={featuredRecipes} />
              <RecipeDivider />
            </>
          )}
          <RecipesGrid
            recipes={flattenedPages}
            isEmpty={isEmpty}
            isLoading={isQueryLoading}
            title={getCardsSectionTitle(debouncedQuery, filtersString)}
            nextPageTrigger={nextPageTrigger}
          />
          {isEmpty && featuredRecipes && (
            <>
              <RecipeDivider />
              <FeaturedRecipes recipes={featuredRecipes} />
            </>
          )}
          {showFooter && <GettingStarted />}
        </Main>
        {showFooter && <Footer />}
      </Layout>
    </AppearanceProvider>
  )
}

export default RecipesView

const Main = styled.main``
